import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { ShieldAlert, Plus, X } from 'lucide-react';

const STATUS_LABEL = {
  active: { text: 'Aktiv', bg: '#dcfce7', color: '#166534' },
  expired: { text: 'Utgången', bg: '#f3f4f6', color: '#374151' },
  revoked: { text: 'Avslutad', bg: '#fee2e2', color: '#991b1b' },
};

function deriveStatus(s) {
  if (s.revoked_at) return 'revoked';
  if (new Date(s.expires_at) <= new Date()) return 'expired';
  return 'active';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('sv-SE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const ActivateModal = ({ open, onClose, onActivated }) => {
  const [customers, setCustomers] = useState([]);
  const [targetOrgId, setTargetOrgId] = useState('');
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/clients')
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCustomers([]));
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 20) {
      toast.error('Anledning måste vara minst 20 tecken');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/admin/support-sessions', {
        target_org_id: targetOrgId,
        reason: reason.trim(),
        ticket_reference: ticket.trim() || null,
      });
      toast.success('Supportläge aktiverat (4 timmar)');
      onActivated();
      onClose();
      setTargetOrgId('');
      setReason('');
      setTicket('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte aktivera supportläge');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Aktivera supportläge</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div
          className="mb-4 p-4 rounded"
          style={{ background: '#fff7ed', borderLeft: '4px solid #fb923c' }}
        >
          <p className="text-sm" style={{ color: '#9a3412' }}>
            <strong>Viktigt:</strong> När du aktiverar detta får du tillgång
            till deltagardata. All åtkomst loggas och visas i kundens audit-log.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Kundorganisation</label>
            <select
              required
              value={targetOrgId}
              onChange={(e) => setTargetOrgId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">— Välj kund —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Anledning ({reason.length}/20 minimum)
            </label>
            <textarea
              required
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Felsöker problem med kartläggningssparande efter kundens supportärende #1234"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Ärendereferens (valfri)</label>
            <input
              type="text"
              value={ticket}
              onChange={(e) => setTicket(e.target.value)}
              placeholder="Ex: SUPPORT-1234"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting || !targetOrgId || reason.trim().length < 20}
              className="px-4 py-2 rounded text-black font-semibold disabled:opacity-50"
              style={{ background: '#F9CF3F' }}
            >
              {submitting ? 'Aktiverar...' : 'Aktivera (4 timmar)'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SupportSessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/support-sessions');
      setSessions(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error('Kunde inte hämta supportsessioner');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Avsluta denna supportsession?')) return;
    try {
      await api.delete(`/admin/support-sessions/${sessionId}`);
      toast.success('Supportsession avslutad');
      load();
    } catch (err) {
      toast.error('Kunde inte avsluta sessionen');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="support-sessions-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShieldAlert size={24} />
              Supportsessioner
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Tidsbegränsad åtkomst till kunddata för supportändamål
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1 px-4 py-2 rounded font-semibold text-black"
            style={{ background: '#F9CF3F' }}
          >
            <Plus size={16} />
            Aktivera supportläge
          </button>
        </div>

        <div
          className="p-4 rounded"
          style={{ background: '#fffbeb', borderLeft: '4px solid #F9CF3F' }}
        >
          <p className="text-sm">
            All åtkomst till deltagardata under supportläge loggas och
            visas i kundens audit-log. Sessionerna är begränsade till 4 timmar.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Laddar...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded border">
            Inga supportsessioner ännu.
          </div>
        ) : (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm" data-testid="support-sessions-table">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold">Kund</th>
                  <th className="px-4 py-2 font-semibold">Anledning</th>
                  <th className="px-4 py-2 font-semibold">Aktiverat</th>
                  <th className="px-4 py-2 font-semibold">Utgår</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Anrop</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const status = deriveStatus(s);
                  const cfg = STATUS_LABEL[status];
                  return (
                    <tr key={s.id} className="border-t">
                      <td className="px-4 py-2 font-medium">
                        {s.target_org_name || s.target_org_id}
                      </td>
                      <td className="px-4 py-2 max-w-md truncate" title={s.reason}>
                        {s.reason}
                      </td>
                      <td className="px-4 py-2">{formatDate(s.activated_at)}</td>
                      <td className="px-4 py-2">{formatDate(s.expires_at)}</td>
                      <td className="px-4 py-2">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: cfg.bg, color: cfg.color }}
                        >
                          {cfg.text}
                        </span>
                      </td>
                      <td className="px-4 py-2">{s.access_count}</td>
                      <td className="px-4 py-2 text-right">
                        {status === 'active' && (
                          <button
                            onClick={() => handleRevoke(s.id)}
                            className="text-sm font-semibold text-red-700 hover:underline"
                          >
                            Avsluta
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <ActivateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onActivated={load}
        />
      </div>
    </DashboardLayout>
  );
};

export default SupportSessionsPage;
